import { Delete as DeleteIcon, Edit as EditIcon, Insights as InsightsIcon } from '@mui/icons-material';
import { IconButton, Typography, Chip, Box, Switch } from '@mui/material';
import { Button } from 'src/UIKit/components';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Breadcrumb } from 'src/components/Breadcrumb';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { CopyToClipboardButton } from 'src/components/CopyToClipboardButton';
import { formatShortUrl } from '../../helpers';
import GridData from 'src/components/GridData/GridData';
import TooltipCell from 'src/components/TooltipCell/TooltipCell';
import { TableHeader } from 'src/components/TableHeader/TableHeader';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { firstLetterCamelCase, getDateTimeUSAFormat, getDateTimeUSAFormatWithTime } from 'src/constants/helpers';
import { useShortLinkViewController } from '../../controllers';
import { ShortLink } from '../../types';
import { selectProfile } from 'src/modules/profile/slices';
import { EditShortLinkModal } from '../EditShortLinkModal/EditShortLinkModal';
import { AnalyticsModal } from '../AnalyticsModal/AnalyticsModal';

export const ShortLinkView = () => {
  const { profile } = useSelector(selectProfile);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ShortLink | null>(null);
  const {
    isPageLoading,
    shortLinkFilter,
    shortLinkList,
    changeShortLinkSearchFilter,
    changeShortLinkSorting,
    changeShortLinkPagination,
    removeShortLink,
    toggleLinkStatus,
    updateShortLink,
    reloadShortLink,
  } = useShortLinkViewController();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const profileRole = useMemo(() => (profile?.role ? profile.role.name : 'bhi_admin'), [profile]);
  const isDeleteEnabled =
    profileRole === 'bhi_admin' ||
    profileRole === 'company_admin' ||
    profileRole === 'company_manager';

  const handleCreateShortUrl = async () => {
    setIsCreateModalOpen(false);
    await reloadShortLink();
  };

  const isExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const handleEditShortLink = (link: ShortLink) => {
    setSelectedLink(link);
    setIsEditModalOpen(true);
  };

  const handleViewAnalytics = (link: ShortLink) => {
    setSelectedLink(link);
    setIsAnalyticsModalOpen(true);
  };

  const handleSaveShortLink = async (
    linkId: string,
    updates: { url: string; expiresAt?: string },
  ) => {
    const payload: { url: string; expiresAt?: string } = { url: updates.url };
    if (updates.expiresAt !== undefined) {
      payload.expiresAt = updates.expiresAt;
    }
    await updateShortLink(linkId, payload);
    setIsEditModalOpen(false);
    setSelectedLink(null);
  };

  // Memoize the columns configuration to prevent recreating on every render
  const columns: (GridColDef<ShortLink> & { initVisibility?: boolean })[] = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Short URL',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          const shortUrl = formatShortUrl(params.row.code);
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                gap: 0.5,
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ flexShrink: 1, minWidth: 0 }}>
                <CopyToClipboardButton text={shortUrl}>
                  <TooltipCell
                    value={shortUrl}
                    variant="body2"
                    maxWidth="100%"
                  />
                </CopyToClipboardButton>
              </Box>
              {isExpired(params.row.expiresAt) && (
                <Box sx={{ flexShrink: 0 }}>
                  <Chip
                    label="Expired"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Box>
              )}
              {!params.row.isActive && (
                <Box sx={{ flexShrink: 0 }}>
                  <Chip
                    label="Inactive"
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>
          );
        },
        minWidth: 220,
        editable: false,
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'url',
        headerName: 'Target URL',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          const url = params.row.url;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
              <CopyToClipboardButton text={url}>
                <TooltipCell value={url} variant="body2" maxWidth="100%" />
              </CopyToClipboardButton>
            </Box>
          );
        },
        minWidth: 240,
        editable: false,
        sortable: true,
        flex: 2,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          const { createdAt } = params.row;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2">{getDateTimeUSAFormat(new Date(createdAt))}</Typography>
            </Box>
          );
        },
        minWidth: 120,
        editable: false,
        sortable: true,
        flex: 1,
      },
      {
        field: 'lastClick',
        headerName: 'Last Click',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          const lastClickTimestamp = params.row.analytics?.lastClickTimestamp;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {!lastClickTimestamp ? (
                <Typography
                  variant="body2"
                  fontStyle="italic"
                  color="text.secondary"
                >
                  Never
                </Typography>
              ) : (
                <Typography variant="body2">{getDateTimeUSAFormat(new Date(lastClickTimestamp))}</Typography>
              )}
            </Box>
          );
        },
        minWidth: 120,
        editable: false,
        sortable: true,
        flex: 1,
      },
      {
        field: 'clickCount',
        headerName: 'Total Clicks',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" fontWeight="bold">
                {params.row.clickCount || 0}
              </Typography>
            </Box>
          );
        },
        minWidth: 80,
        editable: false,
        sortable: true,
        flex: 0.5,
      },
      {
        field: 'expiresAt',
        headerName: 'Expires At',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          const { expiresAt } = params.row;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {!expiresAt ? (
                <Typography
                  variant="body2"
                  fontStyle="italic"
                  color="text.secondary"
                >
                  Never
                </Typography>
              ) : (
                <Typography variant="body2">{getDateTimeUSAFormatWithTime(new Date(expiresAt))}</Typography>
              )}
            </Box>
          );
        },
        minWidth: 160,
        editable: false,
        sortable: true,
        flex: 1,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        renderCell: (params: GridRenderCellParams<ShortLink>) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2">
                {firstLetterCamelCase(
                  `${params.row.createdBy?.firstName ?? ''} ${
                    params.row.createdBy?.lastName ?? ''
                  }`,
                )}
              </Typography>
            </Box>
          );
        },
        minWidth: 150,
        editable: false,
        sortable: true,
        flex: 1,
        initVisibility: false,
      },
    ],
    [formatShortUrl, isExpired, firstLetterCamelCase, getDateTimeUSAFormat],
  );

  return (
    <>
      <ConfirmationDialog />

      <EditShortLinkModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        mode="create"
        isLinkTrackingPage={true}
        onCreate={handleCreateShortUrl}
        disablePortal={true}
      />

      <EditShortLinkModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveShortLink}
        shortLink={selectedLink}
        isLinkTrackingPage={true}
        disablePortal={true}
      />

      <AnalyticsModal
        open={isAnalyticsModalOpen}
        onClose={() => setIsAnalyticsModalOpen(false)}
        shortLink={selectedLink}
      />

      <>
        <PrivateLayoutView
          title={
            <Breadcrumb items={[{ label: 'Link Tracking', isActive: true }]} />
          }
        >
          <GridData
            header={
              <TableHeader
                searchValue={shortLinkFilter.query}
                onSearchChange={changeShortLinkSearchFilter}
                actions={
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    variant="contained"
                  >
                    Create Short URL
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={shortLinkList?.items}
            columns={columns}
            renderAction={(r: ShortLink) => {
              return (
                <>
                  <IconButton size="small" color="primary" onClick={() => handleViewAnalytics(r)}>
                    <InsightsIcon fontSize="inherit" />
                  </IconButton>

                  <IconButton size="small" color="primary" onClick={() => handleEditShortLink(r)}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>

                  <Switch
                    size="small"
                    checked={r.isActive}
                    onChange={() => {
                      if (r.isActive) {
                        openDialog({
                          title: `Deactivate Short URL`,
                          content: 'Are you sure you want to deactivate this short URL?',
                          onConfirm: () => toggleLinkStatus(r.id, r.url, false),
                        });
                      } else {
                        openDialog({
                          title: `Activate Short URL`,
                          content: 'Are you sure you want to activate this short URL?',
                          onConfirm: () => toggleLinkStatus(r.id, r.url, true),
                        });
                      }
                    }}
                  />

                  {isDeleteEnabled && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        openDialog({
                          title: `Delete Short URL`,
                          content: 'Are you sure you want to delete this short URL?',
                          onConfirm: () => removeShortLink(r.id),
                        })
                      }
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  )}
                </>
              );
            }}
            sorting={{
              value: shortLinkFilter.sort,
              onChange: changeShortLinkSorting,
            }}
            pagination={{
              page: shortLinkFilter.page || 0,
              pageSize: shortLinkFilter.limit || 25,
              totalCount: shortLinkList?.count,
              onChange: changeShortLinkPagination,
            }}
          />
        </PrivateLayoutView>
      </>
    </>
  );
};
